import React from 'react';

import utcToZonedTime from 'date-fns-tz/utcToZonedTime';
import format from 'date-fns/format';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';

import CopyIcon from '@atlaskit/icon/glyph/copy';
import EditorDoneIcon from '@atlaskit/icon/glyph/editor/done';
import QuestionCircle from '@atlaskit/icon/glyph/question-circle';
import { Box, Flex, Text, xcss } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import { useResource } from '@atlassian/jira-router';
import { transitioningDetailsResource } from '@atlassian/jira-router-resources-eoc-unlock-operations';
import { IconBox, IconBoxColor } from '../../../../common/ui/icon-box';

import { useUserTimezone } from '../../../../controllers/timezone';
import { messages } from './messages';

export const StepFour = () => {
	const { formatMessage } = useIntl();
	const { timeZone } = useUserTimezone();
	const { data: transitioningData } = useResource(transitioningDetailsResource);

	if (!transitioningData) {
		throw new Error('[StepFour] component should not be rendered before details resource fetched');
	}

	const items = [
		{
			icon: { component: CopyIcon, color: IconBoxColor.Magenta },
			text: formatMessage(messages.listItemOne),
		},
		{
			icon: { component: EditorDoneIcon, color: IconBoxColor.Green },
			text: formatMessage(messages.listItemTwo),
		},
		{
			icon: { component: QuestionCircle, color: IconBoxColor.Purple },
			text: formatMessage(messages.listItemThree, {
				unlockDate: format(
					utcToZonedTime(transitioningData?.autoOgTurnOffDate, timeZone),
					'MMMM dd, yyyy',
				),
				b: (chunks: React.ReactNode) => <b>{chunks}</b>,
			}),
		},
	];

	return (
		<Flex direction="column" rowGap="space.300">
			<Flex rowGap="space.100" direction="column">
				<Heading size="xlarge">{formatMessage(messages.title)}</Heading>
			</Flex>
			<Flex role="list" direction="column" rowGap="space.150">
				<Text>{formatMessage(messages.listDescription)}</Text>
				{items.map((item, i) => (
					<Flex
						justifyContent="start"
						columnGap="space.075"
						alignItems="start"
						role="listitem"
						key={i}
					>
						<IconBox icon={item.icon.component} color={item.icon.color} />
						<Box>{item.text}</Box>
					</Flex>
				))}
				<Text>
					{formatMessage(messages.learnMore, {
						link: (str: string[]) =>
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							(
								<Button
									appearance="link"
									spacing="none"
									href="https://support.atlassian.com/jira-service-management-cloud/docs/what-are-operations/"
									target="_blank"
								>
									<Box as="span" xcss={buttonTextStyles}>
										{str}
									</Box>
								</Button> // eslint-disable-next-line @typescript-eslint/no-explicit-any
							) as any,
					})}
				</Text>
			</Flex>
		</Flex>
	);
};

const buttonTextStyles = xcss({
	textDecoration: 'underline',
	fontWeight: 400,
});
